import { useMemo } from "react";
import { Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Notify } from "@/components/ui/notify";
import { Tag } from "@/components/ui/tag";
import { ProductionLineCanvas } from "@/features/lines/canvas/ProductionLineCanvas";
import { useScenario } from "@/features/simulations/hooks";
import type { SimulationDataSource, SimulationPerMachine } from "@/types/simulations.types";

interface CandidateAsLineCardProps {
  scenarioId: string;
  lineId: string;
  dataSources: SimulationDataSource[] | null;
  perMachine?: SimulationPerMachine[];
}

// Lê o candidato pela linguagem da tela de linha. A topologia
// do cenário == linha base (overrides só mexem em params), então o canvas vivo o representa.
// Destaque/sinalização via legenda adjacente (não invasiva ao React Flow):
//  - "ajustada": máquina com overrides no snapshot do cenário;
//  - "premissa": máquina que entrou com fallback (dataSources[].defaulted).
export function CandidateAsLineCard({
  scenarioId,
  lineId,
  dataSources,
  perMachine,
}: CandidateAsLineCardProps) {
  const scenarioQuery = useScenario(scenarioId);
  const { data: scenario } = scenarioQuery;

  const nameById = useMemo(
    () => new Map((perMachine ?? []).map((p) => [p.machineId, p.name])),
    [perMachine]
  );

  const adjusted = useMemo(
    () =>
      (scenario?.machineOverrides ?? [])
        .filter((m) => (m.simOverrides?.length ?? 0) > 0)
        .map((m) => m.machineId),
    [scenario]
  );

  const premise = useMemo(
    () =>
      Array.from(
        new Set(
          (dataSources ?? [])
            .filter((d) => d.defaulted && d.machineId)
            .map((d) => d.machineId as string)
        )
      ),
    [dataSources]
  );

  if (scenarioQuery.isError) {
    return (
      <Notify.QueryError error={scenarioQuery.error} onRetry={() => void scenarioQuery.refetch()} />
    );
  }

  const label = (mid: string) => nameById.get(mid) ?? mid.slice(0, 8);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Candidato como linha</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <ProductionLineCanvas lineId={lineId} mode="display" height={320} hideMiniMap />

        <div className="flex flex-wrap gap-4 text-sm">
          <div>
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Máquinas ajustadas
            </span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {adjusted.length > 0 ? (
                adjusted.map((m) => <Tag key={m}>{label(m)}</Tag>)
              ) : (
                <span className="text-xs text-muted-foreground">Nenhuma</span>
              )}
            </div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Máquinas em premissa
            </span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {premise.length > 0 ? (
                premise.map((m) => <Tag key={m}>{label(m)}</Tag>)
              ) : (
                <span className="text-xs text-muted-foreground">Nenhuma</span>
              )}
            </div>
          </div>
        </div>

        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Máquinas ajustadas tiveram parâmetros variados no cenário. Máquinas em premissa entraram
          com fallback conservador (sem dado informado), o que reduz a confiança.
        </p>
      </CardContent>
    </Card>
  );
}
